import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileOutlined from "../../assets/FileOutlined.svg";
import TrashOutlined from "../../assets/TrashIconOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { useUserDisplayName } from "../../hooks/useUserDisplayName";
import { getCookie } from "../../utils/Common";
import { SUPPORTED_DOCUMENT_ACCEPT, partitionBySupportedFileType } from "../../utils/fileValidation";
import {
  WaiverServiceRecord,
  WaiverChatLine,
  getWaiverChatLines,
  isWaiverChatUnread,
  sendWaiverChatLine,
  downloadWaiverChatDocument,
  markWaiverChatAsRead,
  getWaiverChatLineTimestamp,
} from "./utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const getInitials = (fullName: string | undefined) => {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

function ChatLineDocuments({
  documentPaths,
  downloadingPath,
  onDownload,
}: {
  documentPaths: string[];
  downloadingPath: string;
  onDownload: (path: string) => void;
}) {
  if (!documentPaths.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
      {documentPaths.map((path, index) => {
        const fileName = path?.split("/")?.pop() ?? path;
        return (
          <div
            key={`${path}-${index}`}
            data-testid="waiver-chat-document-item"
            style={{
              borderRadius: "4px",
              border: "1px solid #DBE2FF",
              background: "#fff",
              display: "flex",
              padding: "6px 8px",
              alignItems: "center",
              gap: "8px",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
              <img
                src={FileOutlined}
                alt="file-icon"
                style={{ width: "14px", height: "14px", marginRight: "8px", flexShrink: 0 }}
              />
              <span
                title={fileName}
                style={{
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </span>
            </div>
            <IconButton
              size="small"
              aria-label="download"
              onClick={() => onDownload(path)}
              disabled={downloadingPath === path}
              data-testid="waiver-chat-download-button"
            >
              {downloadingPath === path ? (
                <CircularProgress size={14} />
              ) : (
                <FileDownloadOutlinedIcon fontSize="small" style={{ color: "#383847" }} />
              )}
            </IconButton>
          </div>
        );
      })}
    </div>
  );
}

// Every line renders at the same full width regardless of its own text/
// document length — width varying line-to-line (e.g. sized to fit a short
// "ok" next to a long paragraph) is what read as uneven/odd, so this
// deliberately does not shrink-to-fit like a typical chat bubble. isOwnMessage
// only tints the box/avatar so a two-party conversation is still readable at
// a glance, without affecting its width.
function ChatLineItem({
  line,
  isOwnMessage,
  downloadingPath,
  onDownload,
}: {
  line: WaiverChatLine;
  isOwnMessage: boolean;
  downloadingPath: string;
  onDownload: (path: string) => void;
}) {
  // partyId is the sender's Keycloak/party id, same kind of id as a waiver's
  // own createdBy/responseBy — resolve it to a display name the same way.
  const senderName = useUserDisplayName(line.partyId);
  return (
    <div
      data-testid="waiver-chat-line"
      style={{
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        background: isOwnMessage ? "#EEF1FF" : "#F8F9FD",
        padding: "10px 12px",
      }}
    >
      <div style={{ fontSize: "14px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {line.line}
      </div>
      <ChatLineDocuments
        documentPaths={line.documentPaths ?? []}
        downloadingPath={downloadingPath}
        onDownload={onDownload}
      />
      <div
        data-testid="waiver-chat-line-author"
        style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}
      >
        <Avatar
          sx={{
            width: 24,
            height: 24,
            fontSize: "11px",
            borderRadius: "12px",
            background: isOwnMessage ? "#3E5EEC" : "#655DBB",
          }}
        >
          {getInitials(senderName)}
        </Avatar>
        <span style={{ fontSize: "12px", fontWeight: 500 }}>{senderName}</span>
        {getWaiverChatLineTimestamp(line) ? (
          <span style={{ fontSize: "11px", color: "#767689" }}>
            &bull; {moment(getWaiverChatLineTimestamp(line)).format("MMM DD, YYYY, hh:mm A")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// Renders the per-waiver chat thread and composer, embedded right after
// WaiverSummary's own card for the same waiver. Kept as its own component
// (rather than folded into WaiverSummary) since it owns an entirely separate
// concern — sending/reading chat lines — with its own network calls and
// local state. The header bar mirrors WaiverRequestForm's own panel-header
// treatment (same padding/border/background) so this reads as one of the
// same family of panels rather than a plain heading bolted underneath.
export default function WaiverChat({ waiver }: { waiver: WaiverServiceRecord }) {
  const { t } = useTranslation();
  const { dynamicConfig } = useContext(RootContext) ?? {};
  const { setLoanDetails } = useContext(LoansContext) ?? {};

  const [message, setMessage] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [downloadingPath, setDownloadingPath] = useState("");

  // Same "current user's party id" resolution used throughout the app
  // (e.g. useAxios's request interceptor) — a cookie set at login, falling
  // back to dynamicConfig for the rare case it isn't set yet.
  const currentPartyId = getCookie("partyId") || dynamicConfig?.partyId;

  const chatLines = getWaiverChatLines(waiver);
  const isUnread = isWaiverChatUnread(waiver, currentPartyId);

  useEffect(() => {
    if (isUnread && waiver.waiverId) {
      markWaiverChatAsRead(setLoanDetails, waiver, currentPartyId);
    }
    // Fires once per waiver/unread-transition, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiver.waiverId, isUnread]);

  // accept only filters the native file-picker dialog — the same allowlist
  // used for waiver-document uploads applies to chat attachments.
  const addFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    const { supported, unsupported } = partitionBySupportedFileType(files);
    setFileError(unsupported.length ? t("unsupportedFileType") : "");
    if (supported.length) setPendingFiles((prev) => [...prev, ...supported]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files ?? []);
    event.target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!message.trim() && pendingFiles.length === 0) return;
    setIsSending(true);
    setSendError("");
    try {
      await sendWaiverChatLine(setLoanDetails, waiver, message.trim(), pendingFiles, currentPartyId);
      setMessage("");
      setPendingFiles([]);
    } catch (err) {
      setSendError(t("waiverChatSendFailed"));
    } finally {
      setIsSending(false);
    }
  };

  const downloadDocument = async (path: string) => {
    setDownloadingPath(path);
    try {
      const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
      const url = await downloadWaiverChatDocument(waiver.waiverId ?? "", path, realmHeaders);
      // Programmatically-clicked <a>, same as WaiverSummary's document
      // download — avoids window.open's popup-blocking/tab-closing issues
      // for presigned GCS URLs.
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error("Something went wrong while downloading a chat document. ERROR:", err);
    } finally {
      setDownloadingPath("");
    }
  };

  return (
    <Box data-testid="waiver-chat" sx={{ padding: "16px" }}>
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            paddingBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ChatBubbleOutlineOutlinedIcon fontSize="small" style={{ color: "#3E5EEC" }} />
            <h3
              style={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                margin: 0,
                padding: 0,
              }}
            >
              {t("waiverChatTitle")}
              {chatLines.length > 0 ? (
                <span style={{ color: "#767689", fontWeight: 400 }}> ({chatLines.length})</span>
              ) : null}
            </h3>
          </div>
          {isUnread ? (
            <Chip
              size="small"
              label={t("waiverChatUnread")}
              data-testid="waiver-chat-unread-chip"
              style={{ background: "#F5A623", color: "#fff" }}
            />
          ) : null}
        </Grid>

        <Grid
          item
          xs={12}
          style={{ display: "flex", flexDirection: "column", gap: "12px" }}
        >
          {chatLines.length === 0 ? (
            <div data-testid="waiver-chat-empty" style={{ fontSize: "12px", color: "#767689" }}>
              {t("waiverChatEmpty")}
            </div>
          ) : (
            chatLines.map((line, index) => (
              <ChatLineItem
                key={line.chatLineOrderId ?? index}
                line={line}
                isOwnMessage={Boolean(currentPartyId) && line.partyId === currentPartyId}
                downloadingPath={downloadingPath}
                onDownload={downloadDocument}
              />
            ))
          )}
        </Grid>

        <Grid item xs={12} style={{ paddingTop: "16px" }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder={t("waiverChatMessagePlaceholder")}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            data-testid="waiver-chat-message-input"
            style={{ background: "#F8F9FD" }}
          />
        </Grid>

        {fileError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>{fileError}</div>
          </Grid>
        ) : null}

        {pendingFiles.length > 0 ? (
          <Grid
            item
            xs={12}
            style={{ paddingTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}
          >
            {pendingFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                data-testid="waiver-chat-pending-file"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  border: "1px solid #B8B8C7",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </span>
                <IconButton
                  size="small"
                  aria-label="remove"
                  onClick={() => handleRemoveFile(index)}
                  data-testid="waiver-chat-remove-file-button"
                >
                  <img src={TrashOutlined} alt="delete-icon" style={{ width: "14px", height: "14px" }} />
                </IconButton>
              </div>
            ))}
          </Grid>
        ) : null}

        {sendError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>{sendError}</div>
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
            paddingTop: "12px",
          }}
        >
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon fontSize="small" />}
            data-testid="waiver-chat-attach-button"
            style={{ borderRadius: "20px", textTransform: "none", borderColor: "#DBE2FF", color: "#383847" }}
          >
            {t("waiverChatAttach")}
            <VisuallyHiddenInput
              type="file"
              accept={SUPPORTED_DOCUMENT_ACCEPT}
              onChange={handleFileUpload}
              multiple
            />
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            endIcon={<SendOutlinedIcon fontSize="small" />}
            loading={isSending}
            disabled={!message.trim() && pendingFiles.length === 0}
            onClick={handleSend}
            data-testid="waiver-chat-send-button"
            style={{ borderRadius: "20px", textTransform: "none", paddingLeft: "18px", paddingRight: "18px" }}
          >
            {t("waiverChatSend")}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
